import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, MapPin, Phone, Facebook } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/product_categories')
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        // Filter out private categories and specific A6/A7 categories to match Navbar
        const filtered = data.filter(cat => 
          !cat.name.da.toLowerCase().includes('private') && 
          !cat.category_id.toLowerCase().includes('private') &&
          !cat.category_id.toLowerCase().includes('blomsterkort-a6') &&
          !cat.category_id.toLowerCase().includes('blomsterkort-a7')
        );
        setCategories(filtered);
      })
      .catch(err => console.error('Error fetching categories:', err));
  }, []);

  return (
    <footer className="bg-brand-50 border-t border-brand-100 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          {/* Brand Column */}
          <div className="space-y-8">
            <Link to="/" className="inline-block relative">
              <span 
                className="text-4xl text-brand-950 inline-block"
                style={{ fontFamily: "'Dancing Script', cursive", transform: "rotate(-3deg)" }}
              >
                CardsbyEve
              </span>
            </Link>
            <p className="text-brand-600 text-sm leading-relaxed font-light">
              Håndtegnede illustrationer og lykønskningskort skabt med kærlighed til den nordiske natur og det gode håndværk. Hvert værk fortæller sin egen unikke historie.
            </p>
            <div className="flex space-x-5">
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brand-400 hover:text-brand-950 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brand-400 hover:text-brand-950 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h4 className="text-brand-950 font-serif text-xl mb-8">Udforsk</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="text-brand-500 hover:text-brand-950 text-sm transition-colors uppercase tracking-widest text-[10px] font-medium">
                  Forside
                </Link>
              </li>
              <li>
                <Link to="/hvem-er-jeg" className="text-brand-500 hover:text-brand-950 text-sm transition-colors uppercase tracking-widest text-[10px] font-medium">
                  Hvem er jeg
                </Link>
              </li>
              <li>
                <Link to="/forhandlere" className="text-brand-500 hover:text-brand-950 text-sm transition-colors uppercase tracking-widest text-[10px] font-medium">
                  Forhandlere
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-brand-500 hover:text-brand-950 text-sm transition-colors uppercase tracking-widest text-[10px] font-medium">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Products Column */}
          <div>
            <h4 className="text-brand-950 font-serif text-xl mb-8">Produkter</h4>
            <ul className="space-y-4">
              <li>
                <Link 
                  to="/produkter/blomsterkort" 
                  className="text-brand-500 hover:text-brand-950 text-sm transition-colors uppercase tracking-widest text-[10px] font-medium"
                >
                  Blomsterkort
                </Link>
              </li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link 
                    to={cat.category_id === 'chokolade' ? '/kollektion/chokolade' : `/produkter/${cat.category_id}`} 
                    className="text-brand-500 hover:text-brand-950 text-sm transition-colors uppercase tracking-widest text-[10px] font-medium"
                  >
                    {cat.name.da}
                  </Link>
                </li>
              ))}
              {categories.length === 0 && (
                <li><span className="text-brand-400 text-xs italic">Henter kategorier...</span></li>
              )}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-brand-950 font-serif text-xl mb-8">Kontakt</h4>
            <ul className="space-y-5">
              <li className="flex items-start space-x-3">
                <Mail className="w-4 h-4 text-brand-950 shrink-0 mt-0.5" />
                <a href="mailto:evelyn@cardsbyeve.dk" className="text-brand-600 hover:text-brand-950 text-sm transition-colors font-light">
                  evelyn@cardsbyeve.dk
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-4 h-4 text-brand-950 shrink-0 mt-0.5" />
                <a href="tel:+4531356040" className="text-brand-600 hover:text-brand-950 text-sm transition-colors font-light">
                  31 35 60 40
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-brand-950 shrink-0 mt-0.5" />
                <span className="text-brand-600 text-sm font-light">
                  7840 Højslev, Danmark
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-12 border-t border-brand-200 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-brand-400 text-[10px] uppercase tracking-[0.2em] font-medium">
            &copy; {currentYear} Cards by Eve. Alle rettigheder forbeholdes.
          </p>
          <div className="flex space-x-8">
            <a href="#" className="text-brand-400 hover:text-brand-950 text-[10px] uppercase tracking-widest font-medium transition-colors">Handelsbetingelser</a>
            <a href="#" className="text-brand-400 hover:text-brand-950 text-[10px] uppercase tracking-widest font-medium transition-colors">Privatlivspolitik</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
